import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown, ChevronRightIcon, PencilIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { StudentBulkUpdate } from '@/components/students/student-bulk-update';
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table';
import { TableCellWithPopover } from '@/components/table/table-cell-with-popover';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Tooltip } from '@/components/tooltip';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { assignManualOnDemandGLA, EmptyArray, getStudentsWithLearningIssues } from '@/lib/api-calls';
import { usePaginatedFilters } from '@/lib/filtering';
import { DEFAULT_PAGE_SIZE, paginationRequestSchema } from '@/lib/pagination';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { normalizeNewlines } from '@/lib/string-utils';
import { AirId, formatInstant, FrequentTimeZones, isAirAdmin, toRawAirId } from '@airreading/common';

import { AirIdDisplay } from '@/components/air-id-display';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker';
import { useToast } from '@/components/ui/hooks/use-toast';
import { useAllSchools, useSchoolAndOrgOptions } from '@/lib/api-call-hooks';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { airServerErrorFrom, axiosErrorFrom, useErrorToast } from '@/lib/error-handling';
import { useSorting } from '@/lib/sorting';
import { getStudentFeedbackStatusOptions } from '@/lib/student-feedback-utils';
import { getStudentYearOptions } from '@/lib/student-utils';
import { airColumnHelper } from '@/lib/tables';

import {
  FluencyIssueLevelFilter,
  GetStudentsWithLearningIssuesSortableColumn,
  getStudentsWithLearningIssuesSortableColumnSchema,
  getStudentsWithLearningIssuesSortOrderSchema,
  MasteryCheckLevelFilter,
  StudentFeedbackStatus,
  StudentFeedbackStatusLabelMap,
  studentFeedbackStatusSchema,
  StudentStatus,
  StudentWithLearningIssue,
  StudentYearColorMap,
  studentYearOption,
  studentYearSchema,
  toSessionFeedbackCompoundId,
} from '@airreading/common/data';

import { useTimeZone } from '@/auth/useTimeZone';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { RightDrawer } from '@/components/ui/right-drawer';
import { CreateStudent } from '@/routes/_layout/users/-components/create-student';
import { CreateStudentsByCsv } from '@/routes/_layout/users/-components/create-students-by-csv';
import { EditStudentFeedbackModal } from '../-components/edit-student-feedback-modal';
import { StudentReviewHistory } from '../-components/student-review-history';
import { studentsTabs } from '../-components/tabs';
import { StudentStatusBadge } from '@/components/students/student-status-badge';

const studentsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  schools: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  issueStartDateFrom: z.string().optional(),
  issueStartDateTo: z.string().optional(),
  issueStatuses: z.array(studentFeedbackStatusSchema).optional(),
  fluencyIssueLevels: z.array(z.enum(['level_too_easy', 'level_too_hard'])).optional(),
  masteryCheckLevels: z.array(z.enum(['lesson_too_easy', 'lesson_too_hard'])).optional(),
  sortBy: getStudentsWithLearningIssuesSortOrderSchema.optional(),
});

const FLUENCY_ISSUE_LEVEL_OPTIONS: { value: FluencyIssueLevelFilter; label: string }[] = [
  { value: FluencyIssueLevelFilter.level_too_easy, label: 'Level too easy' },
  { value: FluencyIssueLevelFilter.level_too_hard, label: 'Level too hard' },
];

const MASTERY_CHECK_LEVEL_OPTIONS: { value: MasteryCheckLevelFilter; label: string }[] = [
  { value: MasteryCheckLevelFilter.lesson_too_easy, label: 'Lesson too easy' },
  { value: MasteryCheckLevelFilter.lesson_too_hard, label: 'Lesson too hard' },
];

export const Route = createFileRoute('/_layout/students/learning-issue/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentsWithLearningIssuesIndex,
  validateSearch: studentsSearchSchema,
});

const col = airColumnHelper<StudentWithLearningIssue>(getStudentsWithLearningIssuesSortableColumnSchema);

// Helper to format note with 2-line truncation and hover tooltip
const NoteCell = ({ note }: { note: string }) => {
  const normalizedNote = normalizeNewlines(note);
  const lines = normalizedNote.split('\n');
  const displayLines = lines.slice(0, 2);
  const hasMore = lines.length > 2 || normalizedNote.length > 100;

  if (!hasMore) {
    return <div className="line-clamp-2">{displayLines.join('\n')}</div>;
  }

  return (
    <Tooltip trigger={<div className="line-clamp-2">{displayLines.join('\n')}</div>}>
      <div className="max-w-md whitespace-pre-wrap">{normalizedNote}</div>
    </Tooltip>
  );
};

// Helper to render clickable dates for session feedbacks
const FeedbackDatesCell = ({
  dates,
  studentId,
  monthDayOnly,
}: {
  dates: Array<{ date: Temporal.Instant | string; sessionId: string; coachId: string }>;
  studentId: string;
  monthDayOnly?: boolean;
}) => {
  const timeZone = FrequentTimeZones.UsCentral;
  if (!dates || dates.length === 0) return <span>-</span>;

  return (
    <div className="flex flex-wrap gap-1 ml-1">
      {dates.map(({ date, sessionId, coachId }, idx) => {
        // Convert string to Temporal.Instant if needed
        const instant = typeof date === 'string' ? Temporal.Instant.from(date) : date;
        const formatted = monthDayOnly
          ? (() => {
              const pd = instant.toZonedDateTimeISO(timeZone).toPlainDate();
              return `${pd.month}/${pd.day}`;
            })()
          : formatInstant(instant, timeZone).formattedDate;
        return (
          <span key={idx}>
            <Link
              to="/sessions/session-feedbacks/$feedbackId"
              params={{ feedbackId: toSessionFeedbackCompoundId({ sessionId, studentId, coachId }) }}
              className="text-blue-600 hover:text-blue-800"
              search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
            >
              {formatted}
            </Link>
            {idx < dates.length - 1 && <span>, </span>}
          </span>
        );
      })}
    </div>
  );
};

function formatCompletedGLADateLabel(e: StudentWithLearningIssue['completedGLADates'][number], tz: string): string {
  const pd = Temporal.Instant.from(e.sessionStart).toZonedDateTimeISO(tz).toPlainDate();
  const dateStr = `${pd.month}/${pd.day}`;
  return `${dateStr} (${e.wpm}, ${e.accuracyPercent}%)`;
}

const CompletedGLADatesCell = ({ entries }: { entries: StudentWithLearningIssue['completedGLADates'] }) => {
  const tz = FrequentTimeZones.UsCentral;
  if (!entries.length) return null;

  const labels = entries.map((e) => formatCompletedGLADateLabel(e, tz));

  return (
    <TableCellWithPopover items={labels} maxVisible={2} renderPopoverContent={() => <div>{labels.join(', ')}</div>} />
  );
};

const LEARNING_ISSUE_DEFINITIONS: ReadonlyArray<{
  label: string;
  getCount: (d: NonNullable<StudentWithLearningIssue['learningIssueData']>) => number;
  getDates: (d: NonNullable<StudentWithLearningIssue['learningIssueData']>) => Array<{
    date: Temporal.Instant | string;
    sessionId: string;
    coachId: string;
  }>;
}> = [
  {
    label: 'Lesson too easy',
    getCount: (d) => d.lessonTooEasyCount,
    getDates: (d) => d.lessonTooEasyDates ?? [],
  },
  {
    label: 'Lesson too hard',
    getCount: (d) => d.lessonTooHardCount,
    getDates: (d) => d.lessonTooHardDates ?? [],
  },
  {
    label: 'Level too easy',
    getCount: (d) => d.levelTooEasyCount,
    getDates: (d) => d.levelTooEasyDates ?? [],
  },
  {
    label: 'Level too hard',
    getCount: (d) => d.levelTooHardCount,
    getDates: (d) => d.levelTooHardDates ?? [],
  },
];

const IssuesCell = ({
  learningIssueData,
  studentId,
}: {
  learningIssueData: StudentWithLearningIssue['learningIssueData'];
  studentId: string;
}) => {
  if (!learningIssueData) {
    return <span>-</span>;
  }

  const applied = LEARNING_ISSUE_DEFINITIONS.filter((def) => def.getCount(learningIssueData) > 0);
  if (applied.length === 0) {
    return <span>-</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      {applied.map((def) => {
        const count = def.getCount(learningIssueData);
        const dates = def.getDates(learningIssueData);
        return (
          <div key={def.label} className="flex flex-wrap items-baseline gap-x-0">
            <span>
              {def.label}({count}):{' '}
            </span>
            <FeedbackDatesCell dates={dates} studentId={studentId} monthDayOnly />
          </div>
        );
      })}
    </div>
  );
};

const buildColumns = (
  schoolIdToDistrictName: Record<string, string>,
  timeZone: string,
  onSelectStudent: (student: StudentWithLearningIssue) => void,
  onEditSuccess: () => void,
  onAssignGLA: (studentId: string) => void,
) => [
  createTableRowSelectionCell(col),
  col.accessor((row) => row, {
    id: 'edit',
    header: '',
    size: 50,
    cell: ({ getValue }) => {
      const student = getValue();
      if (!student.learningIssueStudentFeedback) {
        return null;
      }
      return (
        <EditStudentFeedbackModal
          studentFeedback={student.learningIssueStudentFeedback}
          trigger={<PencilIcon size={16} className="cursor-pointer" />}
          onSuccess={onEditSuccess}
        />
      );
    },
  }),
  col.accessor((row) => row, {
    id: 'fullNameOrEmail',
    header: 'Student name',
    size: 150,
    cell: ({ getValue }) => {
      const student = getValue();
      return (
        <span className="text-blue-600 hover:text-blue-800 cursor-pointer" onClick={() => onSelectStudent(student)}>
          {student.fullNameOrEmail}
        </span>
      );
    },
  }),
  col.accessor('grade', {
    id: 'grade',
    header: 'Grade',
    size: 80,
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <div className="min-w-[80px] flex justify-start">
          <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
            {studentYearOption(grade)}
          </span>
        </div>
      );
    },
  }),

  col.accessor((row) => row, {
    id: 'school',
    header: 'School - District',
    size: 200,
    enableSorting: false,
    cell: ({ getValue }) => {
      const school = getValue()?.school;
      const districtName = school?.id ? schoolIdToDistrictName[school.id] : null;
      return (
        school && (
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name} {districtName ? `- ${districtName}` : ''}
          </Link>
        )
      );
    },
  }),
  col.accessor('status', {
    id: 'status',
    header: 'Status',
    size: 120,
    cell: ({ getValue }) => {
      return (
        <div className="flex gap-x-1">
          <StudentStatusBadge status={getValue()} />
        </div>
      );
    },
  }),
  col.accessor((row) => row.learningIssueData?.issueStartDate, {
    id: 'issueStartDate',
    header: 'Issue start date',
    size: 120,
    cell: ({ getValue }) => {
      const issueStartDate = getValue();
      if (!issueStartDate) return <span>-</span>;
      // Convert string to Temporal.Instant if needed
      const instant = typeof issueStartDate === 'string' ? Temporal.Instant.from(issueStartDate) : issueStartDate;
      return <span>{formatInstant(instant, timeZone).formattedDate}</span>;
    },
  }),
  col.accessor((row) => row, {
    id: 'issues',
    header: 'Issues',
    size: 340,
    enableSorting: false,
    cell: ({ getValue }) => {
      const student = getValue();
      return <IssuesCell learningIssueData={student.learningIssueData} studentId={student.id} />;
    },
  }),
  col.accessor((row) => row.learningIssueData?.reviewer, {
    id: 'reviewer',
    header: 'Reviewer',
    size: 150,
    cell: ({ getValue }) => {
      const reviewer = getValue();
      if (!reviewer) return <span>-</span>;
      const name = [reviewer.firstName, reviewer.lastName].filter(Boolean).join(' ').trim();
      return <span>{name || reviewer.email}</span>;
    },
  }),
  col.accessor((row) => row.learningIssueData?.issueStatus, {
    id: 'issueStatus',
    header: 'Issue status',
    size: 120,
    cell: ({ getValue }) => {
      const status = getValue();
      if (!status) return <span>{StudentFeedbackStatusLabelMap[StudentFeedbackStatus.need_review]}</span>;
      return <span>{StudentFeedbackStatusLabelMap[status]}</span>;
    },
  }),
  col.accessor((row) => row.learningIssueData?.note ?? '', {
    id: 'issueNotes',
    header: 'Issue notes',
    size: 250,
    cell: ({ getValue }) => {
      const note = getValue();
      return <NoteCell note={note} />;
    },
  }),
  col.accessor((row) => row.nextScheduledSessionLesson, {
    id: 'nextScheduledSessionLesson',
    header: 'Next lesson',
    size: 200,
    enableSorting: false,
    cell: ({ getValue }) => {
      const lesson = getValue();
      if (!lesson?.id) return <span>-</span>;
      return (
        <Link to="/lessons/$lessonId" params={{ lessonId: lesson.id }} className="text-blue-600 hover:text-blue-800">
          {lesson.name}
        </Link>
      );
    },
  }),
  col.accessor('totalFutureSessions', {
    id: 'totalFutureSessions',
    header: 'Total future sessions',
    size: 110,
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor((row) => row.nextGLASessionStartInstant, {
    id: 'nextGLASessionStartInstant',
    header: 'Next GLA date',
    size: 130,
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return <span>-</span>;
      return <span>{formatInstant(Temporal.Instant.from(value), timeZone).formattedDate}</span>;
    },
  }),
  col.accessor((row) => row, {
    id: 'completedGLADates',
    header: 'Completed GLA dates',
    size: 220,
    enableSorting: false,
    cell: ({ getValue }) => {
      const student = getValue();
      return <CompletedGLADatesCell entries={student.completedGLADates} />;
    },
  }),
  col.accessor('canLaunchManualGLA', {
    id: 'launchGLA',
    header: 'Launch GLA',
    cell: ({ getValue, row }) => {
      const canLaunch = getValue();
      const button = (
        <Button
          size="sm"
          disabled={!canLaunch}
          onClick={(e) => {
            e.stopPropagation();
            onAssignGLA(row.original.id);
          }}
        >
          Assign
        </Button>
      );
      if (canLaunch) return button;
      return <Tooltip trigger={button}>Not eligible</Tooltip>;
    },
  }),

  col.accessor((row) => row.learningIssueStudentFeedback, {
    id: 'newStartingLesson',
    header: 'New starting lesson',
    size: 240,
    enableSorting: false,
    cell: ({ getValue }) => {
      const feedback = getValue();
      const learningPath = feedback?.learningPath;
      const startingLesson = feedback?.startingLesson;
      if (!learningPath?.name || !startingLesson?.name || !startingLesson.id) {
        return '-';
      }
      return (
        <span className="text-sm">
          {learningPath.name}{' '}
          <Link
            to="/lessons/$lessonId"
            params={{ lessonId: startingLesson.id }}
            className="text-blue-600 hover:text-blue-800"
          >
            {startingLesson.name}
          </Link>
        </span>
      );
    },
  }),
  col.accessor((row) => row.learningIssueData?.activeClassIds, {
    id: 'activeClassIds',
    header: 'Class ID',
    size: 150,
    cell: ({ getValue }) => {
      const activeClassIds = getValue();
      if (!activeClassIds || activeClassIds.length === 0) return <span>-</span>;
      return (
        <div className="flex flex-wrap gap-2">
          {activeClassIds.map((classId) => (
            <AirIdDisplay key={classId} id={AirId('cls', toRawAirId(classId))} />
          ))}
        </div>
      );
    },
  }),
];

function StudentsWithLearningIssuesIndex() {
  const { timeZone } = useTimeZone();
  const { data: allSchools } = useAllSchools();
  const { toast } = useToast();
  const [selectedStudent, setSelectedStudent] = useState<StudentWithLearningIssue | null>(null);

  const schoolIdToDistrictName = useMemo(
    () => Object.fromEntries(allSchools.map((school) => [school.id, school.org.name])),
    [allSchools],
  );

  const handleSelectStudent = useCallback((student: StudentWithLearningIssue) => {
    setSelectedStudent(student);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedStudent(null);
  }, []);

  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentsWithLearningIssuesSortableColumn>([
    sortBy,
    didChangeSortBy,
  ]);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery, activeUserNameQuery], setUserNameQuery],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      issueStartDateFrom: [[selectedIssueStartDateFrom], setIssueStartDateFrom],
      issueStartDateTo: [[selectedIssueStartDateTo], setIssueStartDateTo],
      issueStatuses: [[selectedIssueStatuses, activeIssueStatuses], setIssueStatuses],
      fluencyIssueLevels: [[selectedFluencyIssueLevels, activeFluencyIssueLevels], setFluencyIssueLevels],
      masteryCheckLevels: [[selectedMasteryCheckLevels, activeMasteryCheckLevels], setMasteryCheckLevels],
    },
    resetFilters,
    activeFiltersCount: issueTypeFiltersActiveCount,
  } = usePaginatedFilters(
    Route.id,
    [
      'query',
      'schools',
      'grades',
      'issueStartDateFrom',
      'issueStartDateTo',
      'issueStatuses',
      'fluencyIssueLevels',
      'masteryCheckLevels',
    ],
    ['fluencyIssueLevels', 'masteryCheckLevels'],
  );

  const allSchoolOptions = useSchoolAndOrgOptions({
    orgIds: [],
  });
  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getStudentsWithLearningIssues({
      pageIndex,
      pageSize,
      filters: {
        query: activeUserNameQuery,
        schools: activeSelectedSchools,
        grades: activeSelectedGrades,
        issueStartDateFrom: selectedIssueStartDateFrom,
        issueStartDateTo: selectedIssueStartDateTo,
        issueStatuses: activeIssueStatuses,
        fluencyIssueLevels: activeFluencyIssueLevels,
        masteryCheckLevels: activeMasteryCheckLevels,
      },
      sortBy,
    }),
  );

  const students = useMemo(() => response?.students || EmptyArray, [response]);
  useErrorToast(error, 'Error loading students. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<StudentWithLearningIssue>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => students.find((student) => student.id === id) as StudentWithLearningIssue | undefined,
    });

  const onSuccess = () => {
    resetAllRowSelection();
    refetch();
  };

  const handleEditSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const { mutate: assignGLA } = useMutation({
    mutationFn: assignManualOnDemandGLA,
    onSuccess: () => {
      toast({ title: 'On-demand GLA is assigned to the next session successfully' });
      refetch();
    },
    onError: (e) => {
      toast({ title: airServerErrorFrom(e)?.userFacingMessage || 'Failed to assign manual GLA' });
    },
  });

  const handleAssignGLA = useCallback((studentId: string) => assignGLA(studentId), [assignGLA]);

  const columns = useMemo(
    () => buildColumns(schoolIdToDistrictName, timeZone, handleSelectStudent, handleEditSuccess, handleAssignGLA),
    [schoolIdToDistrictName, timeZone, handleSelectStudent, handleEditSuccess, handleAssignGLA],
  );

  const table = useReactTable({
    data: students,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: response?.total || 0,
    onRowSelectionChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    enableColumnResizing: true,
    ...tableSortingOptions,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name or email"
          value={userNameQuery ?? ''}
          onChange={(event) => {
            setUserNameQuery(event.target.value);
          }}
        />
        <TableMoreFiltersMenu
          triggerLabel="Issue"
          showPlusTrigger
          activeFiltersCount={issueTypeFiltersActiveCount}
          filterItems={[
            {
              title: 'Mastery check',
              filterComponent: (
                <TableFilterDropdown
                  title="Mastery check"
                  options={MASTERY_CHECK_LEVEL_OPTIONS}
                  selectedValues={selectedMasteryCheckLevels ?? []}
                  onChange={setMasteryCheckLevels}
                />
              ),
            },
            {
              title: 'Fluency issue',
              filterComponent: (
                <TableFilterDropdown
                  title="Fluency issue"
                  options={FLUENCY_ISSUE_LEVEL_OPTIONS}
                  selectedValues={selectedFluencyIssueLevels ?? []}
                  onChange={setFluencyIssueLevels}
                />
              ),
            },
          ]}
        />
        <TableFilterDropdown
          title="School"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedIssueStartDateFrom}
          toPlainDateString={selectedIssueStartDateTo}
          onRangeSelect={(range) => {
            setIssueStartDateFrom(range.fromPlainDateString);
            setIssueStartDateTo(range.toPlainDateString);
          }}
          placeholder="Issue start date"
        />
        <TableFilterDropdown
          title="Issue status"
          options={getStudentFeedbackStatusOptions().filter(
            (option) => option.value !== StudentFeedbackStatus.resolved,
          )}
          selectedValues={selectedIssueStatuses ?? []}
          onChange={setIssueStatuses}
        />
        <TableFilterDropdown
          title="Grade"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      issueTypeFiltersActiveCount,
      selectedFluencyIssueLevels,
      selectedMasteryCheckLevels,
      setMasteryCheckLevels,
      setFluencyIssueLevels,
      userNameQuery,
      setUserNameQuery,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      selectedIssueStartDateFrom,
      selectedIssueStartDateTo,
      setIssueStartDateFrom,
      setIssueStartDateTo,
      selectedIssueStatuses,
      setIssueStatuses,
      selectedGrades,
      setSelectedGrades,
      resetFilters,
    ],
  );

  const bulkActionsMenuItems = (
    <>
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to drop"
        invalidMessage="Only enrolled students can be selected."
        options={{ status: StudentStatus.dropped }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark drop off"
        validation={(student) => student.status === StudentStatus.enrolled}
      />
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to conclude the program for"
        invalidMessage="Only students with no program concluded status can be selected."
        options={{ status: StudentStatus.program_concluded }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark program concluded"
        validation={(student) => student.status !== StudentStatus.program_concluded}
      />
    </>
  );

  const topBarRightComponents = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add students
          <ChevronDown className="mr-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <CreateStudent
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a student</DropdownMenuItem>}
        />
        <CreateStudentsByCsv
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <InfoPage headerText="Students" tabs={studentsTabs('Learning Issue')}>
      <EntityTable
        table={table}
        showPagination={true}
        isLoading={isPending}
        hasError={!!error}
        emptyState={'No students found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={topBarRightComponents}
      />
      {selectedStudent && (
        <RightDrawer
          open
          onOpenChange={(open) => {
            if (!open) {
              handleCloseDrawer();
            }
          }}
          title={
            <div>
              <div className="text-[30px] font-semibold mb-2 max-[900px]:text-left flex items-center gap-4">
                Review history
                <Link
                  to={'/students/$studentId'}
                  params={{ studentId: selectedStudent.id }}
                  search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
                  className="text-[21px] leading-[38px] text-blue-600 hover:text-blue-800 flex items-center gap-2"
                >
                  {selectedStudent.fullNameOrEmail} <ChevronRightIcon size={18} strokeWidth={3} />
                </Link>
              </div>
            </div>
          }
          shouldConfirmOnCloseModal={false}
          dismissible={false}
          widthClass="bg-[#F7F7F9] w-[40%] min-w-[900px] max-w-[90%] max-[900px]:w-[100%] max-[900px]:min-w-[480px] max-[900px]:max-w-full max-[900px]:sm:max-w-full"
        >
          {() => {
            return (
              <InfoPageSuspenseBoundary
                onError={(e) => {
                  const axiosError = axiosErrorFrom(e);
                  const airServerError = airServerErrorFrom(e);

                  return {
                    allowRetry: axiosError?.response?.status !== 404,
                    errorHeader:
                      airServerError?.userFacingMessage ||
                      'Failed to load student details. Please try again or contact Air Reading support',
                  };
                }}
              >
                <StudentReviewHistory studentId={selectedStudent.id} />
              </InfoPageSuspenseBoundary>
            );
          }}
        </RightDrawer>
      )}
    </InfoPage>
  );
}
